/**
 * A spinner: something is happening and will finish on its own.
 *
 * A faint ring with one arc turning. `size` sm 12 · md 16 · lg 32; `tone`
 * accent (the default), soft (on a raised plate's quiet line), ink (inside
 * the gold button, in its ink), warning (something is off and being put
 * right — the rail's reconnecting mark) or night (the board's lavender, on a
 * night board — the lobby's count at zero). It is decoration when words beside it say
 * what is happening — "Investing…"; alone it needs a `label`, which makes it a
 * status. A status mounted with its words is not reliably heard, so one that
 * appears as its wait begins carries no label: a StatusLine beside it, mounted
 * with it, says the wait (live-region-inserted-with-its-words reads a labelled
 * Spinner as the region it is). Under reduced motion the ring holds still.
 */

import styles from './spinner.module.css';

const SIZE = {
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
} as const;

const TONE = {
  accent: styles.accent,
  soft: styles.soft,
  ink: styles.ink,
  warning: styles.warning,
  night: styles.night,
} as const;

interface SpinnerProps {
  size?: keyof typeof SIZE;
  tone?: keyof typeof TONE;
  /** Says what is happening, when nothing beside the spinner does. */
  label?: string;
  className?: string;
}

export function Spinner({ size = 'md', tone = 'accent', label, className = '' }: SpinnerProps) {
  return (
    <span
      className={[styles.spinner, SIZE[size], TONE[tone], className].filter(Boolean).join(' ')}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}
